import { PlusIcon } from '@langchain/untitled-ui-icons';

import { parseCustomFilter } from '@/Pages/DatasetSessionCompare/utils/parseCustomFilter';
import { FeedbackChip } from '@/components/FeedbackChips';
import { Tooltip } from '@/design-system';
import CheckIcon from '@/icons/CheckIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { DatasetSessionCompareFilterItem } from '../../DatasetSessionCompareFilterItem';

export const FeedbackFilter = ({
  label,
  values,
  handleFilterChange,
  onChange,
  currentFilters,
  customFilters,
  setCustomFilters,
}: {
  label: string;
  values: string[];
  handleFilterChange: (value: string) => void;
  onChange: (value: string[]) => void;
  currentFilters: string[];
  customFilters: Record<string, string[]>;
  setCustomFilters: (value: Record<string, string[]>) => void;
}) => {
  return (
    <div key={label}>
      <DatasetSessionCompareFilterItem
        label={
          <FeedbackChip feedback_key={label.slice('feedback_key:'.length)} />
        }
        selected={values.includes(label)}
        onChange={handleFilterChange}
        value={label}
      />
      <p className="px-4 py-1 text-xs text-quaternary">
        {`Filter format: operator (<, <=, =>, >, =) numerical value (0.3), eg. > 0.3`}
      </p>
      {label in customFilters &&
        customFilters[label].map((f, i) => {
          return (
            <FilterItem
              key={i}
              label={label}
              filter={f}
              index={i}
              values={values}
              customFilters={customFilters}
              setCustomFilters={setCustomFilters}
              currentFilters={currentFilters}
              onChange={onChange}
            />
          );
        })}
      <AddFilterButton
        label={label}
        customFilters={customFilters}
        setCustomFilters={setCustomFilters}
      />
    </div>
  );
};

function FilterItem({
  label,
  filter,
  index,
  values,
  customFilters,
  setCustomFilters,
  currentFilters,
  onChange,
}: {
  label: string;
  filter: string;
  index: number;
  values: string[];
  customFilters: Record<string, string[]>;
  setCustomFilters: (value: Record<string, string[]>) => void;
  currentFilters: string[];
  onChange: (value: string[]) => void;
}) {
  const parsedFilter = parseCustomFilter(
    filter,
    label.slice('feedback_key:'.length)
  );
  const selected = !!(parsedFilter && values.includes(parsedFilter));
  const error = parsedFilter === null && filter !== '';
  return (
    <div key={index}>
      <div
        className={cn(
          'relative mb-1 cursor-default select-none pl-10 ui-active:bg-secondary-hover'
        )}
      >
        <Tooltip side="bottom" align="start" title={error && 'Invalid syntax'}>
          <div>
            <FeedbackFilterInput
              label={label}
              selected={selected}
              parsedFilter={parsedFilter}
              error={error}
              filter={filter}
              index={index}
              customFilters={customFilters}
              setCustomFilters={setCustomFilters}
              currentFilters={currentFilters}
              onChange={onChange}
            />
          </div>
        </Tooltip>
        <FilterCheckbox
          selected={selected}
          parsedFilter={parsedFilter}
          currentFilters={currentFilters}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

function FilterCheckbox({
  selected,
  parsedFilter,
  currentFilters,
  onChange,
}: {
  selected: boolean;
  parsedFilter: string | null;
  currentFilters: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <span
      className={cn('absolute inset-y-0 left-0 flex items-center pl-3')}
      onClick={() => {
        if (selected) {
          onChange([...currentFilters.filter((i) => i !== parsedFilter)]);
        } else if (parsedFilter) {
          onChange([...currentFilters, parsedFilter]);
        }
      }}
    >
      <div
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-[4px] border border-secondary text-brand-green-400',
          selected && 'border-brand bg-brand-tertiary'
        )}
      >
        {selected && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
      </div>
    </span>
  );
}

function FeedbackFilterInput({
  label,
  selected,
  parsedFilter,
  error,
  filter,
  index,
  customFilters,
  setCustomFilters,
  currentFilters,
  onChange,
}: {
  label: string;
  selected: boolean;
  parsedFilter: string | null;
  error: boolean;
  filter: string;
  index: number;
  customFilters: Record<string, string[]>;
  setCustomFilters: (value: Record<string, string[]>) => void;
  currentFilters: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <input
      autoFocus
      aria-disabled={false}
      onBlur={() => {
        if (filter === '') {
          const newCustomFilters = {
            ...customFilters,
          };
          newCustomFilters[label] = newCustomFilters[label].filter(
            (_, idx) => idx !== index
          );
          setCustomFilters(newCustomFilters);
        }
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          if (selected) {
            onChange([...currentFilters.filter((i) => i !== parsedFilter)]);
          } else if (parsedFilter) {
            onChange([...currentFilters, parsedFilter]);
          }
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      placeholder="Enter filter value"
      value={filter}
      className={cn(
        'my-1 w-full bg-transparent py-[2px] text-sm focus:pb-0 focus:outline-none',
        error ? 'border-b-2 border-error' : 'border-brand focus:border-b-2'
      )}
      onChange={(e) => {
        e.stopPropagation();

        if (selected) {
          const newParsedFilter = parseCustomFilter(
            e.target.value,
            label.slice('feedback_key:'.length)
          );
          if (newParsedFilter) {
            onChange([
              ...currentFilters.filter((i) => i !== parsedFilter),
              newParsedFilter,
            ]);
          } else {
            onChange([...currentFilters.filter((i) => i !== parsedFilter)]);
          }
        }

        const newCustomFilters = {
          ...customFilters,
        };
        newCustomFilters[label][index] = e.target.value;
        setCustomFilters(newCustomFilters);
      }}
    />
  );
}

function AddFilterButton({
  label,
  customFilters,
  setCustomFilters,
}: {
  label: string;
  customFilters: Record<string, string[]>;
  setCustomFilters: (value: Record<string, string[]>) => void;
}) {
  return (
    <div
      onClick={() => {
        const newCustomFilters = { ...customFilters };
        for (const [key, value] of Object.entries(newCustomFilters)) {
          newCustomFilters[key] = value.filter((i) => i !== '');
        }

        if (!(label in newCustomFilters)) {
          newCustomFilters[label] = [''];
        } else {
          newCustomFilters[label].push('');
        }
        setCustomFilters(newCustomFilters);
      }}
      className="relative mt-[1px] inline-flex w-full cursor-pointer items-center justify-start gap-2 py-1 pl-3 hover:bg-secondary-hover active:bg-secondary"
    >
      <div className="inline-flex h-4 w-4 items-center justify-center rounded border border-ls-gray-100 p-px">
        <PlusIcon className="size-4" />
      </div>
      <div className="text-sm font-semibold leading-tight text-tertiary">
        Add filter
      </div>
    </div>
  );
}
